import { useEffect, useState } from "react";
import { API, LOCALITIES, CTA_MODE } from "@/config";
import { WhatsAppButton } from "@/components/Cta";
import { Car, KeyRound, CheckCircle2 } from "lucide-react";

const INTENTS = [
  { value: "need_spot", label: "Rent a bay for my car", icon: Car },
  { value: "have_spot", label: "List a bay I own", icon: KeyRound },
];

export const LeadForm = () => {
  const [intent, setIntent] = useState("need_spot");
  const [values, setValues] = useState({ name: "", phone: "", locality: "", spots: "", note: "" });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    const handler = (e) => setIntent(e.detail);
    window.addEventListener("nb:set-intent", handler);
    return () => window.removeEventListener("nb:set-intent", handler);
  }, []);

  const set = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (values.name.trim().length < 2) next.name = "Please tell us your name.";
    const digits = values.phone.replace(/[^\d+]/g, "");
    if (!/^\+?\d{10,15}$/.test(digits)) next.phone = "Enter a valid 10-digit mobile number";
    if (intent === "have_spot" && values.spots && Number(values.spots) < 1)
      next.spots = "Enter at least 1 bay.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!validate()) return;
    setSubmitting(true);
    const body = {
      name: values.name.trim(),
      phone: values.phone.replace(/[^\d+]/g, ""),
      intent,
    };
    if (values.locality) body.locality = values.locality;
    if (intent === "have_spot" && values.spots) body.spots = Number(values.spots);
    if (values.note.trim()) body.note = values.note.trim();

    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data.ok) {
        setDone({ name: body.name.split(" ")[0], phone: body.phone });
        return;
      }
      if (res.status === 400) {
        const msg = typeof data.detail === "string" ? data.detail : "Please check your details.";
        if (/mobile|phone/i.test(msg)) setErrors({ phone: msg });
        else setFormError(msg);
      } else if (res.status === 422) {
        const next = {};
        (Array.isArray(data.detail) ? data.detail : []).forEach((d) => {
          const field = d.loc?.[1];
          if (field) next[field] = d.msg || "Please check this field.";
        });
        if (next.phone) next.phone = "Enter a valid 10-digit mobile number";
        if (next.name) next.name = "Please tell us your name.";
        setErrors(Object.keys(next).length ? next : { name: "Please check your details." });
      } else if (res.status === 429) {
        setFormError(
          typeof data.detail === "string"
            ? data.detail
            : "Too many enquiries from this device. Try again later.",
        );
      } else {
        setFormError("Something went wrong. Please WhatsApp us instead.");
      }
    } catch {
      setFormError("We couldn't reach our servers. Please WhatsApp us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const Err = ({ field }) =>
    errors[field] ? (
      <p data-testid={`lead-error-${field}`} className="mt-2 text-sm text-err">
        {errors[field]}
      </p>
    ) : null;

  return (
    <section data-testid="lead-section" id="lead" className="nb-section">
      <div className="nb-shell max-w-[820px]">
        <p className="nb-eyebrow">Get a callback</p>
        <h2 className="nb-h2 mt-4">
          {CTA_MODE === "app" ? "Not ready to sign up? Get a callback" : "Tell us what you need"}
        </h2>
        <p className="nb-sub mt-4">
          No app, no account, no payment. One call from a human, usually within a working day.
        </p>

        {done ? (
          <div data-testid="lead-success" className="nb-card mt-10 animate-nb-rise border-ok/50">
            <CheckCircle2 size={26} className="text-ok" aria-hidden="true" />
            <h3 className="mt-4 text-xl">Got it, {done.name}.</h3>
            <p className="mt-3 text-base text-ink2">
              We'll call you on <span className="nb-num">{done.phone}</span> within one working day.
              If you'd rather talk now, WhatsApp us.
            </p>
            <div className="mt-6">
              <WhatsAppButton
                kind={intent === "have_spot" ? "host" : "renter"}
                label="WhatsApp +91 97411 46164"
                testId="lead-success-whatsapp"
              />
            </div>
          </div>
        ) : (
          <form data-testid="lead-form" onSubmit={onSubmit} noValidate className="nb-card mt-10">
            <fieldset>
              <legend className="font-head text-sm tracking-[0.1em] text-ink3">I want to…</legend>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {INTENTS.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    data-testid={`lead-intent-${value}`}
                    aria-pressed={intent === value}
                    onClick={() => setIntent(value)}
                    className={`flex min-h-[64px] items-center gap-3 rounded-nb1 border px-4 py-3 text-left text-sm font-medium transition-colors ${
                      intent === value
                        ? "border-brand bg-brand/10 text-ink"
                        : "border-lineStrong bg-surface3 text-ink2 hover:border-brand/60"
                    }`}
                  >
                    <Icon size={18} className={intent === value ? "text-brand" : "text-ink3"} />
                    {label}
                  </button>
                ))}
              </div>
            </fieldset>

            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="nb-name" className="block text-sm font-medium text-ink2">
                  Your name
                </label>
                <input
                  id="nb-name"
                  data-testid="lead-name"
                  className="nb-input mt-2"
                  placeholder="As it appears on your ID"
                  value={values.name}
                  onChange={set("name")}
                  aria-invalid={!!errors.name}
                />
                <Err field="name" />
              </div>
              <div>
                <label htmlFor="nb-phone" className="block text-sm font-medium text-ink2">
                  Mobile number
                </label>
                <input
                  id="nb-phone"
                  data-testid="lead-phone"
                  type="tel"
                  inputMode="tel"
                  className="nb-input mt-2 font-mono"
                  placeholder="9741146164"
                  value={values.phone}
                  onChange={set("phone")}
                  aria-invalid={!!errors.phone}
                />
                {errors.phone ? (
                  <Err field="phone" />
                ) : (
                  <p className="mt-2 text-xs text-ink3">
                    We call and WhatsApp on this number. Nothing else.
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="nb-locality" className="block text-sm font-medium text-ink2">
                  Locality
                </label>
                <select
                  id="nb-locality"
                  data-testid="lead-locality"
                  className="nb-input mt-2"
                  value={values.locality}
                  onChange={set("locality")}
                >
                  <option value="">Select a locality</option>
                  {LOCALITIES.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                  <option value="Somewhere else">Somewhere else</option>
                </select>
              </div>
              {intent === "have_spot" && (
                <div>
                  <label htmlFor="nb-spots" className="block text-sm font-medium text-ink2">
                    How many bays?
                  </label>
                  <input
                    id="nb-spots"
                    data-testid="lead-spots"
                    type="number"
                    min="1"
                    max="500"
                    className="nb-input mt-2 font-mono"
                    value={values.spots}
                    onChange={set("spots")}
                  />
                  {errors.spots ? (
                    <Err field="spots" />
                  ) : (
                    <p className="mt-2 text-xs text-ink3">Bays at this one address</p>
                  )}
                </div>
              )}
              <div className="sm:col-span-2">
                <label htmlFor="nb-note" className="block text-sm font-medium text-ink2">
                  Anything we should know? <span className="text-ink3">(optional)</span>
                </label>
                <textarea
                  id="nb-note"
                  data-testid="lead-note"
                  rows="3"
                  className="nb-input mt-2"
                  placeholder="e.g. Basement bay, gate closes at 11pm, need it from 1 August"
                  value={values.note}
                  onChange={set("note")}
                />
              </div>
            </div>

            {formError && (
              <p data-testid="lead-form-error" className="mt-5 rounded-nb1 border border-err/50 bg-err/10 px-4 py-3 text-sm text-ink">
                {formError}
              </p>
            )}

            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                type="submit"
                data-testid="lead-submit"
                disabled={submitting}
                className="nb-btn-primary disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Get a callback"}
              </button>
              <span className="text-sm text-ink3">Prefer to just message?</span>
              <WhatsAppButton
                kind={intent === "have_spot" ? "host" : "renter"}
                label="WhatsApp +91 97411 46164"
                testId="lead-whatsapp"
              />
            </div>

            <p className="mt-6 text-xs text-ink3">
              By submitting you agree we may call or WhatsApp you about NightBay. We don't sell your
              number. See our{" "}
              <a href="/privacy" className="text-brand underline">
                privacy terms
              </a>
              .
            </p>
          </form>
        )}
      </div>
    </section>
  );
};
